import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useT } from '../i18n';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

// Known row fields of GET /api/reports/{id}/generate, in display order.
const KNOWN = ['date', 'campaign', 'stage', 'clicks', 'unique_clicks', 'conversions', 'cr', 'revenue', 'cost', 'profit', 'roi'];
const PERCENT = new Set(['cr', 'roi']);
const HIDDEN = new Set(['id', '_id', 'campaign_id', 'cost_currency', 'revenue_by_currency']);

/** Columns of the report rows: known fields first, then status_* and the rest; objects are left out. */
export function reportColumns(rows) {
  const seen = [];
  for (const row of rows || []) {
    for (const [k, v] of Object.entries(row || {})) {
      if (HIDDEN.has(k) || seen.includes(k)) continue;
      if (v !== null && typeof v === 'object') continue;
      seen.push(k);
    }
  }
  const known = KNOWN.filter((k) => seen.includes(k));
  const statuses = seen.filter((k) => k.startsWith('status_')).sort();
  const rest = seen.filter((k) => !KNOWN.includes(k) && !k.startsWith('status_'));
  return [...known, ...statuses, ...rest];
}

export function formatCell(key, value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') {
    const s = value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return PERCENT.has(key) ? s + '%' : s;
  }
  if (typeof value === 'boolean') return value ? '✓' : '—';
  return String(value);
}

const day = (iso) => (iso ? String(iso).slice(0, 10) : '');

/*
  Saved reports: "Open" shows the report on the screen. Same data as the CSV and
  XLSX downloads (they use the same server function).
*/
export default function SavedReportView({ report, open, onOpenChange }) {
  const { t } = useT();
  const [state, setState] = useState('idle'); // idle | loading | ready | error
  const [result, setResult] = useState(null);

  const load = async () => {
    if (!report || !report.id) return;
    setState('loading');
    try {
      const res = await axios.get(`${API}/reports/${report.id}/generate`);
      setResult(res.data || null);
      setState('ready');
    } catch (err) {
      console.error('Error generating report:', err);
      setState('error');
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open, report && report.id]);

  const label = (k) => {
    const names = {
      date: t('Report date'),
      campaign: t('Report campaign'),
      stage: t('Report stage'),
      clicks: t('Report clicks'),
      unique_clicks: t('Report unique clicks'),
      conversions: t('Report conversions'),
      cr: 'CR',
      revenue: t('Report revenue'),
      cost: t('Report cost'),
      profit: t('Report profit'),
      roi: 'ROI',
    };
    if (names[k]) return names[k];
    if (k.startsWith('status_')) return t('Status: {status}', { status: k.slice(7) });
    return k;
  };

  const rows = (result && Array.isArray(result.data) && result.data) || [];
  const columns = reportColumns(rows);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{(report && report.name) || t('Report')}</DialogTitle>
          <DialogDescription>
            {result && result.date_range
              ? t('Period {start} — {end}, time zone {tz}, currency {currency}.', {
                  start: day(result.date_range.start),
                  end: day(result.date_range.end),
                  tz: result.timezone || 'UTC',
                  currency: result.currency || '—',
                })
              : t('The same data as in the CSV and XLSX downloads.')}
          </DialogDescription>
        </DialogHeader>
        {state === 'loading' || state === 'idle' ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="report-view-loading">
            {t('Building the report…')}
          </p>
        ) : state === 'error' ? (
          <div className="py-8 text-center" data-testid="report-view-error">
            <p className="text-sm text-destructive">{t('Could not build the report.')}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={load}>
              {t('Try loading again')}
            </Button>
          </div>
        ) : !rows.length ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="report-view-empty">
            {t('No data for this period.')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="report-view-table">
              <TableHeader>
                <TableRow>
                  {columns.map((c) => (
                    <TableHead key={c} className="whitespace-nowrap">
                      {label(c)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={i}>
                    {columns.map((c) => (
                      <TableCell key={c} className="whitespace-nowrap">
                        {formatCell(c, row[c])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {result && result.fx_rates_available === false ? (
              <p className="mt-2 text-xs text-muted-foreground">
                {t('Exchange rates were not available: amounts in other currencies are not converted.')}
              </p>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
